import { List } from 'immutable'
import { SET_AUTH_DATA } from 'services/auth/actions'
import { SET_APP_BOOTSTRAP_PHASE } from 'services/app/actions'
import { get as getConfig } from 'config'
import _get from 'lodash/get'
import _find from 'lodash/find'
import { getAuthIsLoggedIn } from 'services/auth'
import { getPrimary as getPrimaryLanguage } from 'services/languages'
import {
  client as apolloClient,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
  APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
} from 'services/apollo-graphql'
import { log as logError } from 'log/error'
import { GET_ORACLE_ACCESS } from './query'
import * as actions from './actions'

const config = getConfig()
const oracleFullAccessActive = !!_get(config, ['features', 'ai', 'fullAccess'])
const oracleAccessAiSpeech = _get(config, ['servicesContentTitleGroupIds', 'aiSpeech'])
const oracleAccessAiTextUnlimited = _get(config, ['servicesContentTitleGroupIds', 'aiTextUnlimited'])

function shouldDetermineOracleAccess ({ state }) {
  const { auth } = state
  const isLoggedIn = getAuthIsLoggedIn(auth)

  return isLoggedIn && oracleFullAccessActive
}

/**
 * Fetch Oracle access permissions from GraphQL
 */
async function fetchOracleAccessData ({ dispatch, state }) {
  const { auth, user } = state
  const authToken = auth.get('jwt')
  const language = getPrimaryLanguage(user.getIn(['data', 'language'], List()))

  try {
    dispatch(actions.setOracleAccessProcessing(true))

    const { data, error } = await apolloClient.query({
      query: GET_ORACLE_ACCESS,
      variables: {
        language: [language, '_primary'],
        titleGroupIds: [oracleAccessAiSpeech, oracleAccessAiTextUnlimited],
      },
      fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
      errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
      context: {
        jwt: authToken,
      },
    }) || { data: { contentList: { content: [] } } }

    // Handle GraphQL errors (when errorPolicy is ALL, error comes in the response)
    if (error) {
      logError('Oracle access GraphQL error', { error })
      dispatch(actions.setOracleAccessError(error))
      dispatch(actions.setOracleAccess({
        aiSpeech: false,
        aiTextUnlimited: false,
      }))
      return
    }

    const aiSpeech = _find(_get(data, ['contentList', 'content'], []), (content) => content.titleGroupId === oracleAccessAiSpeech) || {}
    const aiTextUnlimited = _find(_get(data, ['contentList', 'content'], []), (content) => content.titleGroupId === oracleAccessAiTextUnlimited) || {}
    const access = {
      aiSpeech: _get(aiSpeech, ['contentPolicy', 'accessCheck', 'allowed'], false),
      aiTextUnlimited: _get(aiTextUnlimited, ['contentPolicy', 'accessCheck', 'allowed'], false),
    }
    dispatch(actions.setOracleAccess(access))
  } catch (error) {
    logError('Oracle access fetch error', { error })
    dispatch(actions.setOracleAccessError(error))
    dispatch(actions.setOracleAccess({
      aiSpeech: false,
      aiTextUnlimited: false,
    }))
  }
}

/**
 * Watcher for SET_AUTH_DATA action
 * Fires when user logs in or JWT changes
 */
/* eslint-disable import/prefer-default-export */
export function watchOracleAccess ({ after }) {
  return after([SET_AUTH_DATA, SET_APP_BOOTSTRAP_PHASE], async ({
    dispatch,
    state,
    prevState,
    action,
  }) => {
    const { type } = action
    const { auth, app } = state
    const { app: prevApp } = prevState
    const authToken = auth.get('jwt')
    const prevBootstrapComplete = prevApp.get('bootstrapComplete')
    const nextBootstrapComplete = app.get('bootstrapComplete')

    if (
      (type === SET_AUTH_DATA && authToken)
      || (type === SET_APP_BOOTSTRAP_PHASE
        && nextBootstrapComplete
        && nextBootstrapComplete !== prevBootstrapComplete)
    ) {
      await fetchOracleAccessData({ dispatch, state })
    }
  })
    .when(shouldDetermineOracleAccess)
}
